import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Mail, Calendar, User, Shield, Activity, Building, Users, FolderOpen } from 'lucide-react';
import { AppDispatch, RootState } from '@/store';
import { fetchUserById } from '@/store/slices/usersSlice';
import { User as UserType, UserRole } from '@/types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface UserDetailModalProps {
  user: UserType;
  onClose: () => void;
}

export const UserDetailModal: React.FC<UserDetailModalProps> = ({ user, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser, isLoading } = useSelector((state: RootState) => state.users);

  useEffect(() => {
    if (user.id) {
      dispatch(fetchUserById(user.id));
    }
  }, [dispatch, user.id]);

  const formatRoleLabel = (role: UserRole) => {
    return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case UserRole.EXECUTIVE:
        return 'bg-blue-100 text-blue-800';
      case UserRole.PMO:
        return 'bg-blue-100 text-blue-800';
      case UserRole.IS_OWNER:
        return 'bg-green-100 text-green-800';
      case UserRole.PROGRAM_LEAD:
        return 'bg-orange-100 text-orange-800';
      case UserRole.FINANCE:
        return 'bg-yellow-100 text-yellow-800';
      case UserRole.VIEWER:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleDescription = (role: UserRole): string => {
    switch (role) {
      case UserRole.EXECUTIVE:
        return 'Portfolio-level view and approvals. Can view all dashboards and approve EPICs.';
      case UserRole.PMO:
        return 'Full planning workflow access. Can manage all planning entities and user accounts.';
      case UserRole.IS_OWNER:
        return 'Manage own Investible Structures, EPICs, and Features. Can create and prioritize work items.';
      case UserRole.PROGRAM_LEAD:
        return 'Manage allocations and delivery status for assigned programs. Can update team assignments.';
      case UserRole.FINANCE:
        return 'Cost profiles and financial data access. Can manage cost profiles and view financial reports.';
      case UserRole.VIEWER:
        return 'Read-only access to assigned entities. Cannot modify any data.';
      default:
        return 'No description available.';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const userDetails = currentUser || user;

  return (
    <div className="space-y-6">
      {/* User Header */}
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
            <User className="w-8 h-8 text-gray-500" />
          </div>
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900">
            {userDetails.firstName} {userDetails.lastName}
          </h2>
          <div className="flex items-center space-x-2 mt-1">
            <Mail className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">{userDetails.email}</span>
          </div>
          <div className="flex items-center space-x-3 mt-2">
            <Badge className={getRoleBadgeColor(userDetails.role)}>
              {formatRoleLabel(userDetails.role)}
            </Badge>
            <Badge className={userDetails.isActive ? 'bg-status-success/20 text-status-success' : 'bg-status-error/20 text-status-error'}>
              {userDetails.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
          <Shield className="w-5 h-5 mr-2" />
          Role & Permissions
        </h3>
        <p className="text-gray-700 mb-3">{getRoleDescription(userDetails.role)}</p>
        
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-500">User ID:</span>
            <p className="text-gray-900 font-mono">{userDetails.id}</p>
          </div>
          <div>
            <span className="font-medium text-gray-500">Status:</span>
            <p className="text-gray-900">{userDetails.isActive ? 'Active' : 'Inactive'}</p>
          </div>
          <div>
            <span className="font-medium text-gray-500">Created:</span>
            <p className="text-gray-900">{new Date(userDetails.createdAt).toLocaleDateString()}</p>
          </div>
          <div>
            <span className="font-medium text-gray-500">Last Updated:</span>
            <p className="text-gray-900">{new Date(userDetails.updatedAt).toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Activity Summary */}
      {currentUser && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
            <Activity className="w-5 h-5 mr-2" />
            Activity Summary
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Owned Investible Structures */}
            {currentUser._count?.ownedInvestibleStructures !== undefined && (
              <div className="bg-white rounded-lg p-3 border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Building className="w-4 h-4 text-blue-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Owned IS</span>
                  </div>
                  <span className="text-lg font-semibold text-gray-900">
                    {currentUser._count.ownedInvestibleStructures}
                  </span>
                </div>
              </div>
            )}

            {/* Led Programs */}
            {currentUser._count?.ledPrograms !== undefined && (
              <div className="bg-white rounded-lg p-3 border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Users className="w-4 h-4 text-green-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Led Programs</span>
                  </div>
                  <span className="text-lg font-semibold text-gray-900">
                    {currentUser._count.ledPrograms}
                  </span>
                </div>
              </div>
            )}

            {/* Created Scenarios */}
            {currentUser._count?.createdScenarios !== undefined && (
              <div className="bg-white rounded-lg p-3 border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FolderOpen className="w-4 h-4 text-blue-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Created Scenarios</span>
                  </div>
                  <span className="text-lg font-semibold text-gray-900">
                    {currentUser._count.createdScenarios}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Owned IS */}
      {currentUser?.ownedInvestibleStructures && currentUser.ownedInvestibleStructures.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            Owned Investible Structures
          </h3>
          <div className="space-y-2">
            {currentUser.ownedInvestibleStructures.map((is) => (
              <div key={is.id} className="flex items-center justify-between bg-white rounded p-2 border">
                <div>
                  <span className="font-medium text-gray-900">{is.name}</span>
                  <Badge className="ml-2 text-xs">
                    {is.category}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Led Programs */}
      {currentUser?.ledPrograms && currentUser.ledPrograms.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            Led Programs
          </h3>
          <div className="space-y-2">
            {currentUser.ledPrograms.map((program) => (
              <div key={program.id} className="flex items-center justify-between bg-white rounded p-2 border">
                <div>
                  <span className="font-medium text-gray-900">{program.name}</span>
                  <Badge className="ml-2 text-xs">
                    Tech
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Created Scenarios */}
      {currentUser?.createdScenarios && currentUser.createdScenarios.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            Recent Scenarios
          </h3>
          <div className="space-y-2">
            {currentUser.createdScenarios.map((scenario) => (
              <div key={scenario.id} className="flex items-center justify-between bg-white rounded p-2 border">
                <div>
                  <span className="font-medium text-gray-900">{scenario.name}</span>
                  {scenario.isBaseline && (
                    <Badge className="ml-2 text-xs bg-blue-100 text-blue-800">
                      Baseline
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end pt-4 border-t border-gray-200">
        <Button onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
};